import { useState, useEffect, useRef, useCallback } from 'react';
import { useSharedWS } from './useSocket';

export interface PresenceUser {
    id: string;
    name: string;
    color: string;
    cursor: { x: number; y: number };
    selection?: { start: number; end: number };
    lastSeen: number;
}

export interface PresenceState {
    users: Map<string, PresenceUser>;
    isConnected: boolean;
    connectionState: 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED';
    retryCount: number;
    lastError?: string;
}

interface UsePresenceOptions {
    url: string;
    userId: string;
    userName: string;
    userColor: string;
    sessionId?: string;
    onUserJoin?: (user: PresenceUser) => void;
    onUserLeave?: (userId: string) => void;
    onCursorMove?: (userId: string, cursor: { x: number; y: number }) => void;
}

let globalConnectionCounter = 0;

export function usePresence(options: UsePresenceOptions): PresenceState & {
    updateCursor: (x: number, y: number) => void;
    updateSelection: (start: number, end: number) => void;
    disconnect: () => void;
    status: 'idle' | 'connecting' | 'open' | 'error';
} {
    const { userId, userName, userColor, sessionId, onUserJoin, onUserLeave, onCursorMove } =
        options;

    const [state, setState] = useState<PresenceState>({
        users: new Map(),
        isConnected: false,
        connectionState: 'CONNECTING',
        retryCount: 0,
    });

    const [status, setStatus] = useState<'idle' | 'connecting' | 'open' | 'error'>('connecting');

    const onUserJoinRef = useRef(onUserJoin);
    const onUserLeaveRef = useRef(onUserLeave);
    const onCursorMoveRef = useRef(onCursorMove);

    useEffect(() => {
        onUserJoinRef.current = onUserJoin;
        onUserLeaveRef.current = onUserLeave;
        onCursorMoveRef.current = onCursorMove;
    });

    const log = useCallback(
        (message: string, level: 'info' | 'warn' | 'error' = 'info') => {
            const timestamp = new Date().toISOString();
            const prefix = `[PRESENCE-${userId}]`;
            console[level](`${timestamp} ${prefix} ${message}`);
        },
        [userId]
    );

    const handleMessage = useCallback(
        (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data);

                switch (message.type) {
                    case 'pong':
                        log('Received pong');
                        break;

                    case 'user_joined':
                        log(`User joined: ${message.user.name}`);
                        setState(prev => {
                            const newUsers = new Map(prev.users);
                            newUsers.set(message.user.id, {
                                ...message.user,
                                lastSeen: Date.now(),
                            });
                            return { ...prev, users: newUsers };
                        });
                        onUserJoinRef.current?.(message.user);
                        break;

                    case 'user_left':
                        log(`User left: ${message.userId}`);
                        setState(prev => {
                            const newUsers = new Map(prev.users);
                            newUsers.delete(message.userId);
                            return { ...prev, users: newUsers };
                        });
                        onUserLeaveRef.current?.(message.userId);
                        break;

                    case 'cursor_update':
                        if (message.userId !== userId) {
                            console.log(
                                `[Client] Received raw coords from ${message.userId}: x=${message.cursor.x}, y=${message.cursor.y}`
                            );
                            setState(prev => {
                                const newUsers = new Map(prev.users);
                                const user = newUsers.get(message.userId);
                                if (user) {
                                    newUsers.set(message.userId, {
                                        ...user,
                                        cursor: message.cursor,
                                        lastSeen: Date.now(),
                                    });
                                } else {
                                    newUsers.set(message.userId, {
                                        id: message.userId,
                                        name: `User-${message.userId.slice(-4)}`,
                                        color: '#10B981',
                                        cursor: message.cursor,
                                        lastSeen: Date.now(),
                                    });
                                }
                                return { ...prev, users: newUsers };
                            });
                            onCursorMoveRef.current?.(message.userId, message.cursor);
                        }
                        break;

                    case 'selection_update':
                        if (message.userId !== userId) {
                            setState(prev => {
                                const newUsers = new Map(prev.users);
                                const user = newUsers.get(message.userId);
                                if (user) {
                                    newUsers.set(message.userId, {
                                        ...user,
                                        selection: message.selection,
                                        lastSeen: Date.now(),
                                    });
                                }
                                return { ...prev, users: newUsers };
                            });
                        }
                        break;

                    case 'presence_state':
                        log(`Received presence state with ${message.users.length} users`);
                        setState(prev => {
                            const newUsers = new Map();
                            message.users.forEach((user: PresenceUser) => {
                                if (user.id !== userId) {
                                    newUsers.set(user.id, { ...user, lastSeen: Date.now() });
                                }
                            });
                            return { ...prev, users: newUsers };
                        });
                        break;

                    case 'status_update':
                        log(
                            `Received status update: ${message.status}, userCount: ${message.userCount}`
                        );
                        break;
                }
            } catch (error) {
                log(`Error parsing message: ${error}`, 'error');
            }
        },
        [userId, log]
    );

    const { send, socket } = useSharedWS('/presence', handleMessage);

    useEffect(() => {
        if (socket?.readyState === WebSocket.OPEN) {
            log('Connected successfully');
            setState(prev => ({
                ...prev,
                isConnected: true,
                connectionState: 'OPEN',
                retryCount: 0,
                lastError: undefined,
            }));
            setStatus('open');

            send({
                type: 'join',
                userId: userId,
                user: { id: userId, name: userName, color: userColor },
            });

            setTimeout(() => {
                send({ type: 'status', status: 'online' });
            }, 200);
        } else if (socket?.readyState === WebSocket.CONNECTING) {
            setState(prev => ({ ...prev, connectionState: 'CONNECTING' }));
            setStatus('connecting');
        } else {
            setState(prev => {
                const currentUsers = Array.from(prev.users.values());
                currentUsers.forEach(user => {
                    onUserLeaveRef.current?.(user.id);
                });

                return {
                    ...prev,
                    isConnected: false,
                    connectionState: 'CLOSED',
                    users: new Map(),
                };
            });
            setStatus('error');
        }
    }, [socket, send, userId, userName, userColor, log]);

    const throttledCursorUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastCursorUpdateRef = useRef<{ x: number; y: number } | null>(null);

    const updateCursor = useCallback(
        (x: number, y: number) => {
            lastCursorUpdateRef.current = { x, y };

            if (throttledCursorUpdateRef.current) {
                return;
            }

            throttledCursorUpdateRef.current = setTimeout(() => {
                if (lastCursorUpdateRef.current) {
                    send({
                        type: 'cursor_update',
                        cursor: lastCursorUpdateRef.current,
                    });
                }
                throttledCursorUpdateRef.current = null;
            }, 50);
        },
        [send]
    );

    const updateSelection = useCallback(
        (start: number, end: number) => {
            send({
                type: 'selection_update',
                selection: { start, end },
            });
        },
        [send]
    );

    const disconnect = useCallback(() => {
        log('Manually disconnecting');
    }, [log]);

    return {
        ...state,
        updateCursor,
        updateSelection,
        disconnect,
        status,
    };
}
